package com.dotdrives.repository

import com.dotdrives.client.ReportClient
import com.dotdrives.model.GiverLite
import com.dotdrives.model.NewReport
import com.dotdrives.model.Report
import com.dotdrives.model.ReportDefaultUpdate
import com.dotdrives.model.ReportFileItem
import com.dotdrives.model.ReportUpdate
import com.dotdrives.model.ShareAudience
import com.dotdrives.model.Target

interface ReportRepository {
    suspend fun boardPage(orgId: String, cursor: com.dotdrives.model.UpdatesCursor? = null,
        filters: com.dotdrives.controller.UpdatesFilters = com.dotdrives.controller.UpdatesFilters(),
    ): Result<com.dotdrives.model.UpdatesBoardPage>

    suspend fun get(orgId: String, reportId: String): Result<Report>

    suspend fun create(orgId: String, report: NewReport): Result<Report>

    suspend fun thread(orgId: String, reportId: String): Result<List<ReportUpdate>>

    suspend fun postUpdate(orgId: String, reportId: String, body: String): Result<ReportUpdate>

    /** `reports._update` is self-scoped for the `field` role (`assignee_user_id = auth.uid()`)
     *  and unrestricted for staff -- see docs/schema.md "Controller ruling: field-role writes".
     *  The RLS predicate is the real enforcement; callers must still not offer this action to a
     *  field rep viewing a colleague's report (see [ReportRepository] callers in the controller). */
    suspend fun setStatus(orgId: String, reportId: String, status: String): Result<Report>

    suspend fun defaultUpdates(orgId: String): Result<List<ReportDefaultUpdate>>

    suspend fun files(orgId: String, reportId: String): Result<List<ReportFileItem>>

    suspend fun targets(orgId: String): Result<List<Target>>

    /** The "pick givers manually" list. Staff-only in practice -- see [sendToGivers]. */
    suspend fun givers(orgId: String): Result<List<GiverLite>>

    /** The "All &lt;target&gt; givers" preset -- distinct givers with a donation designated to
     *  [targetId], plus how many of them have an active recurring gift. */
    suspend fun targetAudience(orgId: String, targetId: String): Result<ShareAudience>

    /** The "Only givers on active drives" preset -- distinct givers on a `drives` row with no
     *  `outcome` and not archived. */
    suspend fun activeDrivesAudience(orgId: String): Result<ShareAudience>

    /** `report_recipients` insert -- staff-only (`has_perm(org_id,'report.write')`), gated in the
     *  UI by `canWrite`/`isStaff`, not by role-branching here. */
    suspend fun sendToGivers(orgId: String, reportId: String, giverIds: List<String>): Result<Int>

    suspend fun markRead(orgId: String, reportId: String): Result<Unit>
}

class SupabaseReportRepository(
    private val client: ReportClient = ReportClient(),
) : ReportRepository {
    override suspend fun boardPage(orgId: String, cursor: com.dotdrives.model.UpdatesCursor?,
        filters: com.dotdrives.controller.UpdatesFilters) = runCatching { client.boardPage(orgId, cursor, filters) }

    override suspend fun get(orgId: String, reportId: String): Result<Report> =
        runCatching { client.get(orgId, reportId) }

    override suspend fun create(orgId: String, report: NewReport): Result<Report> =
        runCatching { client.create(orgId, report) }

    override suspend fun thread(orgId: String, reportId: String): Result<List<ReportUpdate>> =
        runCatching { client.thread(orgId, reportId) }

    override suspend fun postUpdate(orgId: String, reportId: String, body: String): Result<ReportUpdate> =
        runCatching { client.postUpdate(orgId, reportId, body) }

    override suspend fun setStatus(orgId: String, reportId: String, status: String): Result<Report> =
        runCatching { client.setStatus(orgId, reportId, status) }

    override suspend fun defaultUpdates(orgId: String): Result<List<ReportDefaultUpdate>> =
        runCatching { client.defaultUpdates(orgId) }

    override suspend fun files(orgId: String, reportId: String): Result<List<ReportFileItem>> =
        runCatching { client.files(orgId, reportId) }

    override suspend fun targets(orgId: String): Result<List<Target>> =
        runCatching { client.targets(orgId) }

    override suspend fun givers(orgId: String): Result<List<GiverLite>> =
        runCatching { client.givers(orgId) }

    override suspend fun targetAudience(orgId: String, targetId: String): Result<ShareAudience> =
        runCatching { client.targetAudience(orgId, targetId) }

    override suspend fun activeDrivesAudience(orgId: String): Result<ShareAudience> =
        runCatching { client.activeDrivesAudience(orgId) }

    override suspend fun sendToGivers(orgId: String, reportId: String, giverIds: List<String>): Result<Int> =
        runCatching { client.sendToGivers(orgId, reportId, giverIds) }

    override suspend fun markRead(orgId: String, reportId: String): Result<Unit> =
        runCatching { client.markRead(orgId, reportId) }
}
